import api from './api';
import type {
  IntegracaoConfigDto,
  IntegracaoConfigCreateDto,
  IntegracaoConfigUpdateDto,
  IntegracaoHistoricoDto,
  IntegracaoHistoricoFiltroDto,
  TesteConexaoIntegracaoResultDto,
  ImportacaoResultDto,
  IntegracaoStatusDto,
  PagedResult,
  SiteConfiguracoesDto,
  SiteConfiguracoesUpdateDto,
  ImportacaoSiteConfigResultDto,
} from '../types/integracoes';

const BASE_URL = '/integracoes/revendamais';

// ==================== RevendaMais ====================

export const revendaMaisApi = {
  // Obtém a configuração atual
  getConfig: async (signal?: AbortSignal): Promise<IntegracaoConfigDto | IntegracaoStatusDto> => {
    const response = await api.get<IntegracaoConfigDto | IntegracaoStatusDto>(
      `${BASE_URL}/config`,
      { signal }
    );
    return response.data;
  },

  // Cria ou atualiza a configuração
  createConfig: async (data: IntegracaoConfigCreateDto): Promise<{ id: number; message: string }> => {
    const response = await api.post<{ id: number; message: string }>(`${BASE_URL}/config`, data);
    return response.data;
  },

  // Atualiza a configuração
  updateConfig: async (data: IntegracaoConfigUpdateDto): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`${BASE_URL}/config`, data);
    return response.data;
  },

  // Remove a configuração
  deleteConfig: async (): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`${BASE_URL}/config`);
    return response.data;
  },

  // Testa a conexão
  testarConexao: async (): Promise<TesteConexaoIntegracaoResultDto> => {
    const response = await api.post<TesteConexaoIntegracaoResultDto>(`${BASE_URL}/testar-conexao`);
    return response.data;
  },

  // Importa veículos
  importarVeiculos: async (): Promise<ImportacaoResultDto> => {
    const response = await api.post<ImportacaoResultDto>(`${BASE_URL}/importar-veiculos`);
    return response.data;
  },

  // Importa clientes
  importarClientes: async (): Promise<ImportacaoResultDto> => {
    const response = await api.post<ImportacaoResultDto>(`${BASE_URL}/importar-clientes`);
    return response.data;
  },

  // Obtém o histórico de sincronizações
  getHistorico: async (
    filtro: IntegracaoHistoricoFiltroDto,
    signal?: AbortSignal
  ): Promise<PagedResult<IntegracaoHistoricoDto>> => {
    const params = new URLSearchParams();
    if (filtro.page) params.append('page', filtro.page.toString());
    if (filtro.pageSize) params.append('pageSize', filtro.pageSize.toString());
    if (filtro.tipoOperacao) params.append('tipoOperacao', filtro.tipoOperacao);
    if (filtro.status) params.append('status', filtro.status);
    if (filtro.dataInicio) params.append('dataInicio', filtro.dataInicio);
    if (filtro.dataFim) params.append('dataFim', filtro.dataFim);

    const response = await api.get<PagedResult<IntegracaoHistoricoDto>>(
      `${BASE_URL}/historico?${params.toString()}`,
      { signal }
    );
    return response.data;
  },

  // Obtém o status de todas as integrações
  getStatus: async (signal?: AbortSignal): Promise<IntegracaoStatusDto[]> => {
    const response = await api.get<IntegracaoStatusDto[]>(`${BASE_URL}/status`, { signal });
    return response.data;
  },

  // ==================== Configurações do Site ====================

  // Obtém as configurações do site
  getSiteConfig: async (signal?: AbortSignal): Promise<SiteConfiguracoesDto> => {
    const response = await api.get<SiteConfiguracoesDto>(`${BASE_URL}/site-config`, { signal });
    return response.data;
  },

  // Importa configurações do site do RevendaMais
  importarSiteConfig: async (): Promise<ImportacaoSiteConfigResultDto> => {
    const response = await api.post<ImportacaoSiteConfigResultDto>(`${BASE_URL}/importar-site-config`);
    return response.data;
  },

  // Atualiza as configurações do site
  updateSiteConfig: async (data: SiteConfiguracoesUpdateDto): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`${BASE_URL}/site-config`, data);
    return response.data;
  },
};

// ==================== Upload de Imagens do Site ====================

export interface UploadImagemResult {
  sucesso: boolean;
  url: string;
  nomeOriginal: string;
  nomeArquivo: string;
  tamanho: number;
  mimeType: string;
}

export const siteUploadApi = {
  // Upload de logo
  uploadLogo: async (arquivo: File): Promise<UploadImagemResult> => {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    const response = await api.post<UploadImagemResult>('/site/upload/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Upload de banner
  uploadBanner: async (arquivo: File): Promise<UploadImagemResult> => {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    const response = await api.post<UploadImagemResult>('/site/upload/banner', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Upload de imagem genérica
  uploadImagem: async (arquivo: File, pasta?: string): Promise<UploadImagemResult> => {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    const params = pasta ? `?pasta=${pasta}` : '';
    const response = await api.post<UploadImagemResult>(`/site/upload/imagem${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Deletar imagem
  deletarImagem: async (url: string): Promise<{ sucesso: boolean; message: string }> => {
    const response = await api.delete<{ sucesso: boolean; message: string }>(
      `/site/upload?url=${encodeURIComponent(url)}`
    );
    return response.data;
  },
};

// ==================== Métricas ====================

export interface EvolucaoMetrica {
  data: string;
  visualizacoes: number;
  cliques: number;
  mensagens: number;
  anunciosAtivos: number;
}

export interface ComparativoPlataforma {
  portal: string;
  totalVisualizacoes: number;
  totalCliques: number;
  totalMensagens: number;
  taxaConversaoMedia: number;
  mediaAnunciosAtivos: number;
  maxAnunciosAtivos: number;
}

export interface VeiculoTop {
  veiculoId: number;
  descricao: string;
  anoModelo: number;
  valorVenda: number;
  fotoPrincipal: string | null;
  totalVisualizacoes: number;
  totalCliques: number;
  totalMensagens: number;
  qtdePlataformas: number;
}

export interface AgendamentoSync {
  id: number;
  portal: string;
  nomePlataforma: string | null;
  ativo: boolean;
  frequencia: string;
  horarioPreferencial: string | null;
  ultimaExecucao: string | null;
  proximaExecucao: string | null;
  statusUltima: string | null;
  erroUltima: string | null;
}

export interface AgendamentoSyncConfig {
  ativo: boolean;
  frequencia: string;
  horarioPreferencial?: string | null;
}

export const metricasApi = {
  // Evolução diária de métricas (para gráfico de linha)
  getEvolucao: async (portal?: string, dias = 30, signal?: AbortSignal): Promise<EvolucaoMetrica[]> => {
    const params = new URLSearchParams();
    if (portal) params.append('portal', portal);
    params.append('dias', dias.toString());
    const response = await api.get<EvolucaoMetrica[]>(`/integracoes/metricas/evolucao?${params.toString()}`, { signal });
    return response.data;
  },

  // Comparativo entre plataformas
  getComparativo: async (dias = 30, signal?: AbortSignal): Promise<ComparativoPlataforma[]> => {
    const response = await api.get<ComparativoPlataforma[]>(`/integracoes/metricas/comparativo?dias=${dias}`, { signal });
    return response.data;
  },

  // Top veículos mais visualizados
  getTopVeiculos: async (limite = 10, signal?: AbortSignal): Promise<VeiculoTop[]> => {
    const response = await api.get<VeiculoTop[]>(`/integracoes/metricas/top-veiculos?limite=${limite}`, { signal });
    return response.data;
  },

  // Registrar snapshot de métricas
  registrarSnapshot: async (): Promise<{ mensagem: string }> => {
    const response = await api.post<{ mensagem: string }>('/integracoes/metricas/registrar-snapshot');
    return response.data;
  },

  // Agendamentos
  getAgendamentos: async (signal?: AbortSignal): Promise<AgendamentoSync[]> => {
    const response = await api.get<AgendamentoSync[]>('/integracoes/metricas/agendamentos', { signal });
    return response.data;
  },

  configurarAgendamento: async (portal: string, config: AgendamentoSyncConfig): Promise<{ mensagem: string }> => {
    const response = await api.post<{ mensagem: string }>(`/integracoes/metricas/agendamentos/${portal}`, config);
    return response.data;
  },
};

// ==================== Relatórios ====================

export const relatoriosApi = {
  // Exportar relatório em Excel
  exportarExcel: async (dias = 30): Promise<void> => {
    const response = await api.get(`/integracoes/relatorios/excel?dias=${dias}`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio_integracoes_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Exportar relatório em PDF
  exportarPdf: async (dias = 30): Promise<void> => {
    const response = await api.get(`/integracoes/relatorios/pdf?dias=${dias}`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio_integracoes_${new Date().toISOString().split('T')[0]}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

export default revendaMaisApi;
